import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import 'vis-timeline/styles/vis-timeline-graph2d.css';
import './scribane-timeline.css';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';

export interface ScenesTimelineScene {
  id: string;
  order: number;
  title: string;
  description?: string;
}

interface ScenesTimelineProps {
  scenes: ScenesTimelineScene[];
  className?: string;
  style?: React.CSSProperties;
  groupBy?: 'none' | 'chapter' | 'character' | 'location';
  groupByChapters?: Set<string>;
  groupByCharacters?: Set<string>;
  groupByLocations?: Set<string>;
  entityRefs?: Array<{ sourceType: string; sourceId: string; targetType: string; targetId: string }>;
  chapters?: Array<{ id: string; title: string }>;
  characters?: Array<{ id: string; name: string }>;
  locations?: Array<{ id: string; name: string }>;
  onSceneClick?: (scene: ScenesTimelineScene) => void;
}

function orderToDate(order: number): Date {
  return new Date(2000, 0, Math.max(1, order));
}

function createSceneItemTemplate(
  item: { order?: number; title?: string; excerpt?: string }
): string {
  const title = `${item.order ?? ''}. ${item.title ?? ''}`.trim();
  const excerptContent = item.excerpt
    ? item.excerpt.replace(/</g, '&lt;').replace(/\n/g, '<br>')
    : '';
  const excerptHtml = item.excerpt
    ? `<div class="scribane-timeline-chapter-item__excerpt">${excerptContent}</div>`
    : '';
  return `<div class="scribane-timeline-chapter-item"><div class="scribane-timeline-chapter-item__title"><strong>${title.replace(/</g, '&lt;')}</strong></div>${excerptHtml}</div>`;
}

function stripHtmlToText(html: string): string {
  if (!html?.trim()) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent ?? div.innerText ?? '').trim();
}

export const ScenesTimeline: React.FC<ScenesTimelineProps> = ({
  scenes,
  className,
  style,
  groupBy = 'none',
  groupByChapters = new Set(),
  groupByCharacters = new Set(),
  groupByLocations = new Set(),
  entityRefs = [],
  chapters = [],
  characters = [],
  locations = [],
  onSceneClick,
}) => {
  const { t } = useLocale();
  const [hoveredItem, setHoveredItem] = useState<{
    itemId: string;
    position: { top: number; left: number };
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<{ destroy: () => void; setOptions: (opts: { height?: string }) => void } | null>(null);
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeHandlerRef = useRef<(() => void) | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const sortedScenes = useMemo(
    () => [...scenes].sort((a, b) => a.order - b.order),
    [scenes]
  );

  const { visGroups, visItems } = useMemo(() => {
    const truncateExcerpt = (s: string, max: number) =>
      s.length <= max ? s : s.slice(0, max - 1).trim() + '…';
    const MAX_EXCERPT = 200;

    const baseItems = sortedScenes.map((scene, index) => {
      const order = scene.order ?? index + 1;
      const excerpt = scene.description?.trim()
        ? truncateExcerpt(stripHtmlToText(scene.description), MAX_EXCERPT)
        : '';
      return {
        id: scene.id,
        content: '',
        start: orderToDate(order),
        end: orderToDate(order + 1),
        order,
        title: scene.title,
        excerpt: excerpt || undefined,
        style: 'min-height: 88px; height: 88px;',
      };
    });

    if (groupBy === 'chapter' && chapters.length > 0) {
      const sceneToChapters = new Map<string, string[]>();
      for (const ref of entityRefs) {
        if (ref.sourceType === 'scene' && ref.targetType === 'manual_chapter') {
          const list = sceneToChapters.get(ref.sourceId) ?? [];
          if (!list.includes(ref.targetId)) list.push(ref.targetId);
          sceneToChapters.set(ref.sourceId, list);
        }
      }
      const chaptersToShow =
        groupByChapters.size > 0
          ? chapters.filter((c) => groupByChapters.has(c.id))
          : chapters.filter((c) =>
              sortedScenes.some(
                (s) => (sceneToChapters.get(s.id) ?? []).includes(c.id)
              )
            );
      const groups = chaptersToShow.map((c) => ({
        id: `chapter:${c.id}`,
        content: c.title,
      }));
      const ungroupedId = 'chapter:__ungrouped__';
      groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      const items = baseItems.map((item) => {
        const chapterIds = sceneToChapters.get(item.id) ?? [];
        const filtered = groupByChapters.size > 0
          ? chapterIds.filter((id) => groupByChapters.has(id))
          : chapterIds;
        const inGroups = filtered.filter((id) => chaptersToShow.some((c) => c.id === id));
        if (inGroups.length === 0) {
          return { ...item, group: ungroupedId, style: 'min-height: 88px; height: 88px;' as const };
        }
        return inGroups.map((chapterId) => ({
          ...item,
          id: `${item.id}__chapter__${chapterId}`,
          group: `chapter:${chapterId}`,
          style: 'min-height: 88px; height: 88px;' as const,
        }));
      }).flat();
      return { visGroups: groups, visItems: items };
    }

    if (groupBy === 'character' && characters.length > 0) {
      const sceneToCharacters = new Map<string, string[]>();
      for (const ref of entityRefs) {
        if (ref.sourceType === 'scene' && ref.targetType === 'character') {
          const list = sceneToCharacters.get(ref.sourceId) ?? [];
          if (!list.includes(ref.targetId)) list.push(ref.targetId);
          sceneToCharacters.set(ref.sourceId, list);
        }
      }
      const charsToShow =
        groupByCharacters.size > 0
          ? characters.filter((c) => groupByCharacters.has(c.id))
          : characters.filter((c) =>
              sortedScenes.some(
                (s) => (sceneToCharacters.get(s.id) ?? []).includes(c.id)
              )
            );
      const groups = charsToShow.map((c) => ({
        id: `character:${c.id}`,
        content: c.name,
      }));
      const ungroupedId = 'character:__ungrouped__';
      groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      const items: Array<typeof baseItems[0] & { group: string }> = [];
      for (const item of baseItems) {
        const charIds = sceneToCharacters.get(item.id) ?? [];
        const filtered = groupByCharacters.size > 0
          ? charIds.filter((id) => groupByCharacters.has(id))
          : charIds;
        const inGroups = filtered.filter((id) => charsToShow.some((c) => c.id === id));
        if (inGroups.length === 0) {
          items.push({ ...item, group: ungroupedId, style: 'min-height: 88px; height: 88px;' });
        } else {
          for (const charId of inGroups) {
            items.push({
              ...item,
              id: `${item.id}__character__${charId}`,
              group: `character:${charId}`,
              style: 'min-height: 88px; height: 88px;',
            });
          }
        }
      }
      return { visGroups: groups, visItems: items };
    }

    if (groupBy === 'location' && locations.length > 0) {
      const sceneToLocations = new Map<string, string[]>();
      for (const ref of entityRefs) {
        if (ref.sourceType === 'scene' && ref.targetType === 'location') {
          const list = sceneToLocations.get(ref.sourceId) ?? [];
          if (!list.includes(ref.targetId)) list.push(ref.targetId);
          sceneToLocations.set(ref.sourceId, list);
        }
      }
      const locsToShow =
        groupByLocations.size > 0
          ? locations.filter((l) => groupByLocations.has(l.id))
          : locations.filter((l) =>
              sortedScenes.some(
                (s) => (sceneToLocations.get(s.id) ?? []).includes(l.id)
              )
            );
      const groups = locsToShow.map((l) => ({
        id: `location:${l.id}`,
        content: l.name,
      }));
      const ungroupedId = 'location:__ungrouped__';
      groups.push({ id: ungroupedId, content: t('timeline_group_ungrouped') });
      const items: Array<typeof baseItems[0] & { group: string }> = [];
      for (const item of baseItems) {
        const locIds = sceneToLocations.get(item.id) ?? [];
        const filtered = groupByLocations.size > 0
          ? locIds.filter((id) => groupByLocations.has(id))
          : locIds;
        const inGroups = filtered.filter((id) => locsToShow.some((l) => l.id === id));
        if (inGroups.length === 0) {
          items.push({ ...item, group: ungroupedId, style: 'min-height: 88px; height: 88px;' });
        } else {
          for (const locId of inGroups) {
            items.push({
              ...item,
              id: `${item.id}__location__${locId}`,
              group: `location:${locId}`,
              style: 'min-height: 88px; height: 88px;',
            });
          }
        }
      }
      return { visGroups: groups, visItems: items };
    }

    return {
      visGroups: [] as { id: string; content: string }[],
      visItems: baseItems.map((item) => ({
        ...item,
        style: 'min-height: 88px; height: 88px;' as const,
      })),
    };
  }, [sortedScenes, groupBy, groupByChapters, groupByCharacters, groupByLocations, entityRefs, chapters, characters, locations, t]);

  const getTimelineHeightPx = useCallback((): string => {
    return `${Math.round(window.innerHeight * 0.5)}px`;
  }, []);

  const resolveItemId = useCallback(
    (visItemId: string): string => {
      const baseId = visItemId.includes('__') ? visItemId.split('__')[0] : visItemId;
      return baseId;
    },
    []
  );

  useEffect(() => {
    if (sortedScenes.length === 0) {
      if (timelineRef.current) {
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
      return;
    }
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;
    const useGroups = visGroups.length > 0;
    const orders = sortedScenes.map((s) => s.order);
    const minOrder = Math.min(...orders);
    const visibleRangeDays = Math.min(4, Math.max(2, sortedScenes.length));
    const windowStart = orderToDate(minOrder);
    const windowEnd = orderToDate(minOrder + visibleRangeDays);
    const initTimeline = (): void => {
      if (cancelled || !containerRef.current) return;
      const el = containerRef.current;
      const tryCreate = (): void => {
        if (cancelled || !containerRef.current) return;
        if (el.offsetWidth === 0 || el.offsetHeight === 0) return;
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        import('vis-timeline/standalone').then(({ Timeline: TimelineClass }) => {
          if (cancelled || !containerRef.current) return;
          const fullMinDate = orderToDate(minOrder - 1);
          const fullMaxDate = orderToDate(minOrder + sortedScenes.length + 2);
          const options = {
            margin: { item: { horizontal: 10, vertical: 6 } },
            showTooltips: false,
            showMinorLabels: true,
            showMajorLabels: false,
            height: getTimelineHeightPx(),
            min: fullMinDate,
            max: fullMaxDate,
            template: (itemData: { order?: number; title?: string; excerpt?: string }) =>
              createSceneItemTemplate(itemData),
            format: {
              minorLabels: (date: Date) => String(new Date(date).getDate()),
            },
            ...(useGroups ? { groupHeightMode: 'fitItems' as const } : {}),
          };
          const timeline = useGroups
            ? new TimelineClass(el, visItems, visGroups, options)
            : new TimelineClass(el, visItems, options);
          const updateHeight = (): void => {
            if (timelineRef.current) {
              timelineRef.current.setOptions({ height: getTimelineHeightPx() });
            }
          };
          resizeHandlerRef.current = updateHeight;
          window.addEventListener('resize', updateHeight);
          timeline.setWindow(windowStart, windowEnd);
          timeline.on('itemover', (props: { item: string; event: MouseEvent }) => {
            if (hoverCloseTimeoutRef.current) {
              clearTimeout(hoverCloseTimeoutRef.current);
              hoverCloseTimeoutRef.current = null;
            }
            setHoveredItem({
              itemId: props.item,
              position: { top: props.event.clientY, left: props.event.clientX },
            });
          });
          timeline.on('itemout', () => {
            hoverCloseTimeoutRef.current = setTimeout(() => {
              setHoveredItem(null);
              hoverCloseTimeoutRef.current = null;
            }, 150);
          });
          if (onSceneClick) {
            timeline.on('click', (props: { item: string }) => {
              const sceneId = resolveItemId(props.item);
              const scene = sortedScenes.find((s) => s.id === sceneId);
              if (scene) onSceneClick(scene);
            });
          }
          timelineRef.current = timeline;
        });
      };
      if (el.offsetWidth > 0 && el.offsetHeight > 0) {
        tryCreate();
        return;
      }
      const observer = new ResizeObserver(() => {
        if (cancelled) return;
        tryCreate();
      });
      resizeObserverRef.current = observer;
      observer.observe(el);
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(initTimeline);
    });
    return () => {
      cancelled = true;
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
        hoverCloseTimeoutRef.current = null;
      }
      setHoveredItem(null);
      if (timelineRef.current) {
        timelineRef.current.destroy();
        timelineRef.current = null;
      }
    };
  }, [sortedScenes, visItems, visGroups, getTimelineHeightPx, resolveItemId, onSceneClick]);

  if (sortedScenes.length === 0) {
    return (
      <div className={`scribane-timeline ${className ?? ''}`.trim()} style={{ ...style, minHeight: 400, textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: '14px' }}>
        <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{t('no_scenes')}</p>
      </div>
    );
  }

  return (
    <div className={`scribane-timeline scribane-timeline--scenes ${className ?? ''}`.trim()} style={{ ...style, minHeight: 400 }}>
      <div ref={containerRef} style={{ flex: 1, minHeight: 400, maxHeight: '50vh' }} />
      {hoveredItem &&
        (() => {
          const scene = sortedScenes.find((s) => s.id === resolveItemId(hoveredItem.itemId));
          if (!scene) return null;
          return (
            <HoverCard open={true}>
              <HoverCardTrigger asChild>
                <div
                  style={{
                    position: 'fixed',
                    top: hoveredItem.position.top,
                    left: hoveredItem.position.left,
                    width: 1,
                    height: 1,
                    pointerEvents: 'none',
                    opacity: 0,
                    zIndex: -1,
                  }}
                />
              </HoverCardTrigger>
              <HoverCardContent
                className="max-w-2xl w-auto px-4 py-2"
                side="bottom"
                align="center"
                sideOffset={8}
                collisionPadding={8}
                onPointerEnter={() => {
                  if (hoverCloseTimeoutRef.current) {
                    clearTimeout(hoverCloseTimeoutRef.current);
                    hoverCloseTimeoutRef.current = null;
                  }
                }}
                onPointerLeave={() => {
                  hoverCloseTimeoutRef.current = setTimeout(() => {
                    setHoveredItem(null);
                    hoverCloseTimeoutRef.current = null;
                  }, 150);
                }}
              >
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground">
                    {t('scene_order')}: {scene.order}
                  </p>
                  <p className="font-semibold leading-tight">{scene.title}</p>
                  {scene.description?.trim() ? (
                    <div
                      className={`${RICH_TEXT_HTML_CLASS} text-muted-foreground [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0`}
                      dangerouslySetInnerHTML={{ __html: scene.description.trim() }}
                    />
                  ) : null}
                </div>
              </HoverCardContent>
            </HoverCard>
          );
        })()}
    </div>
  );
};
